export type RedirectMethod = "HEAD" | "GET";

export type RedirectHeaderEntry = {
  name: string;
  value: string;
};

export type RedirectHop = {
  index: number;
  url: string;
  method: RedirectMethod;
  status: number;
  statusText: string;
  ok: boolean;
  location?: string;
  resolvedLocation?: string;
  durationMs: number;
  headers: RedirectHeaderEntry[];
};

export type RedirectTerminalReason =
  | "complete"
  | "redirect-loop"
  | "too-many-redirects"
  | "blocked-redirect"
  | "redirect-without-location";

export type RedirectStatusType = "permanent" | "temporary" | "special" | "not-redirect";

export type RedirectChainFindingStatus = "pass" | "warning" | "fail" | "info";

export type RedirectChainFinding = {
  id: string;
  title: string;
  status: RedirectChainFindingStatus;
  summary: string;
  recommendation: string;
  evidence?: string;
};

export type RedirectQueryDelta = {
  preserved: boolean;
  initial: string;
  final: string;
  added: string[];
  removed: string[];
  changed: string[];
};

export type RedirectUrlDelta = {
  initialProtocol: string;
  finalProtocol: string;
  initialHostname: string;
  finalHostname: string;
  initialPathname: string;
  finalPathname: string;
  protocolChanged: boolean;
  upgradedToHttps: boolean;
  downgradedToHttp: boolean;
  hostnameChanged: boolean;
  pathChanged: boolean;
  queryChanged: boolean;
  trailingSlashChanged: boolean;
};

export type RedirectHopDiagnostic = {
  hopIndex: number;
  statusType: RedirectStatusType;
  fromUrl: string;
  toUrl?: string;
  relativeLocation: boolean;
  protocolChange: "same" | "upgrade" | "downgrade" | "changed" | "unknown";
  hostnameChanged: boolean;
  pathChanged: boolean;
  queryChanged: boolean;
  queryAdded: string[];
  queryRemoved: string[];
  queryValueChanged: string[];
  trailingSlashChanged: boolean;
  setsCookie: boolean;
  cacheControl?: string;
  expires?: string;
  cdnCacheStatus?: string;
};

export type RedirectStatusBreakdown = {
  permanent: number;
  temporary: number;
  special: number;
  finalSuccess: boolean;
  finalError: boolean;
};

export type RedirectHeaderSignals = {
  finalContentType?: string;
  finalCacheControl?: string;
  finalServer?: string;
  finalVia?: string;
  finalCdnCacheStatus?: string;
};

export type RedirectChainDiagnostics = {
  urlDelta: RedirectUrlDelta;
  queryDelta: RedirectQueryDelta;
  hopDiagnostics: RedirectHopDiagnostic[];
  statusBreakdown: RedirectStatusBreakdown;
  uniqueHosts: string[];
  uniqueProtocols: string[];
  redirectCookieHops: number[];
  cacheableTemporaryRedirects: number[];
  headerSignals: RedirectHeaderSignals;
};

export type RedirectChainAnalysis = {
  score: number;
  grade: string;
  summary: string;
  finalUrl: string;
  finalStatus: number;
  redirectCount: number;
  totalDurationMs: number;
  terminalReason: RedirectTerminalReason;
  findings: RedirectChainFinding[];
  topFixes: RedirectChainFinding[];
  diagnostics: RedirectChainDiagnostics;
};

export type RedirectChainAnalysisInput = {
  requestedUrl: string;
  finalUrl: string;
  terminalReason: RedirectTerminalReason;
  hops: RedirectHop[];
};

export function isRedirectStatus(status: number) {
  return status >= 300 && status < 400;
}

export function redirectStatusType(status: number): RedirectStatusType {
  if (status === 301 || status === 308) {
    return "permanent";
  }

  if (status === 302 || status === 303 || status === 307) {
    return "temporary";
  }

  if (status === 300 || status === 304) {
    return "special";
  }

  return "not-redirect";
}

export function statusTone(status: number) {
  if (status >= 200 && status < 300) {
    return "success";
  }

  if (isRedirectStatus(status)) {
    return "redirect";
  }

  if (status >= 400 && status < 500) {
    return "client-error";
  }

  if (status >= 500) {
    return "server-error";
  }

  return "other";
}

function safeUrl(input: string) {
  try {
    return new URL(input);
  } catch {
    return null;
  }
}

function hostnameOf(input: string) {
  return safeUrl(input)?.hostname.toLowerCase() ?? "";
}

function protocolOf(input: string) {
  return safeUrl(input)?.protocol.toLowerCase() ?? "";
}

function searchOf(input: string) {
  return safeUrl(input)?.search ?? "";
}

function pathnameOf(input: string) {
  return safeUrl(input)?.pathname ?? "";
}

function finalHop(hops: RedirectHop[]) {
  return hops[hops.length - 1];
}

function scoreGrade(score: number) {
  if (score >= 95) {
    return "A+";
  }

  if (score >= 88) {
    return "A";
  }

  if (score >= 78) {
    return "B";
  }

  if (score >= 65) {
    return "C";
  }

  if (score >= 50) {
    return "D";
  }

  return "F";
}

function analysisSummary(grade: string, redirectCount: number, terminalReason: RedirectTerminalReason) {
  if (terminalReason !== "complete") {
    return "The redirect trace did not complete cleanly. Fix the terminal redirect issue first.";
  }

  if (grade === "A+" || grade === "A") {
    return redirectCount
      ? "Clean redirect path with acceptable SEO and timing characteristics."
      : "No redirects were needed and the final response is healthy.";
  }

  if (grade === "B") {
    return "Generally healthy redirect path with a few cleanup opportunities.";
  }

  if (grade === "C" || grade === "D") {
    return "Redirect behavior works, but there are SEO, protocol, status, or timing issues to resolve.";
  }

  return "Redirect behavior needs attention before search engines and users can reliably reach the final URL.";
}

function createFinding(
  id: string,
  title: string,
  status: RedirectChainFindingStatus,
  summary: string,
  recommendation: string,
  evidence?: string,
): RedirectChainFinding {
  return {
    id,
    title,
    status,
    summary,
    recommendation,
    evidence,
  };
}

function headerValue(hop: RedirectHop | undefined, name: string) {
  return hop?.headers.find((header) => header.name.toLowerCase() === name.toLowerCase())?.value;
}

function cdnCacheStatus(hop: RedirectHop | undefined) {
  return (
    headerValue(hop, "cf-cache-status") ??
    headerValue(hop, "x-cache") ??
    headerValue(hop, "x-cache-status") ??
    headerValue(hop, "x-vercel-cache") ??
    headerValue(hop, "x-served-by")
  );
}

function querySignature(url: URL) {
  const valuesByKey = new Map<string, string[]>();

  for (const [key, value] of url.searchParams.entries()) {
    const values = valuesByKey.get(key) ?? [];

    values.push(value);
    valuesByKey.set(key, values);
  }

  return valuesByKey;
}

function sortedKeys(valuesByKey: Map<string, string[]>) {
  return [...valuesByKey.keys()].sort((left, right) => left.localeCompare(right));
}

function normalizedQueryValue(values: string[] | undefined) {
  return (values ?? []).join("\u0000");
}

function compareQueryStrings(initialUrl: string, finalUrl: string): RedirectQueryDelta {
  const initial = safeUrl(initialUrl);
  const final = safeUrl(finalUrl);

  if (!initial || !final) {
    return {
      preserved: initialUrl === finalUrl,
      initial: "",
      final: "",
      added: [],
      removed: [],
      changed: [],
    };
  }

  const initialQuery = querySignature(initial);
  const finalQuery = querySignature(final);
  const initialKeys = sortedKeys(initialQuery);
  const finalKeys = sortedKeys(finalQuery);
  const added = finalKeys.filter((key) => !initialQuery.has(key));
  const removed = initialKeys.filter((key) => !finalQuery.has(key));
  const changed = initialKeys.filter(
    (key) =>
      finalQuery.has(key) &&
      normalizedQueryValue(initialQuery.get(key)) !== normalizedQueryValue(finalQuery.get(key)),
  );

  return {
    preserved: !added.length && !removed.length && !changed.length,
    initial: initial.search,
    final: final.search,
    added,
    removed,
    changed,
  };
}

function protocolChange(
  fromUrl: URL | null,
  toUrl: URL | null,
): RedirectHopDiagnostic["protocolChange"] {
  if (!fromUrl || !toUrl) {
    return "unknown";
  }

  if (fromUrl.protocol === toUrl.protocol) {
    return "same";
  }

  if (fromUrl.protocol === "http:" && toUrl.protocol === "https:") {
    return "upgrade";
  }

  if (fromUrl.protocol === "https:" && toUrl.protocol === "http:") {
    return "downgrade";
  }

  return "changed";
}

function trailingSlashChanged(fromUrl: URL | null, toUrl: URL | null) {
  if (!fromUrl || !toUrl) {
    return false;
  }

  return (
    fromUrl.pathname.replace(/\/$/, "") === toUrl.pathname.replace(/\/$/, "") &&
    fromUrl.pathname.endsWith("/") !== toUrl.pathname.endsWith("/")
  );
}

function buildUrlDelta(initialUrl: string, finalUrl: string): RedirectUrlDelta {
  const initial = safeUrl(initialUrl);
  const final = safeUrl(finalUrl);
  const queryDelta = compareQueryStrings(initialUrl, finalUrl);
  const initialProtocol = initial?.protocol ?? "";
  const finalProtocol = final?.protocol ?? "";

  return {
    initialProtocol,
    finalProtocol,
    initialHostname: initial?.hostname.toLowerCase() ?? "",
    finalHostname: final?.hostname.toLowerCase() ?? "",
    initialPathname: initial?.pathname ?? "",
    finalPathname: final?.pathname ?? "",
    protocolChanged: initialProtocol !== finalProtocol,
    upgradedToHttps: initialProtocol === "http:" && finalProtocol === "https:",
    downgradedToHttp: initialProtocol === "https:" && finalProtocol === "http:",
    hostnameChanged:
      (initial?.hostname.toLowerCase() ?? "") !== (final?.hostname.toLowerCase() ?? ""),
    pathChanged: (initial?.pathname ?? "") !== (final?.pathname ?? ""),
    queryChanged: !queryDelta.preserved,
    trailingSlashChanged: trailingSlashChanged(initial, final),
  };
}

function cacheMaxAgeSeconds(cacheControl: string | undefined) {
  if (!cacheControl) {
    return null;
  }

  const maxAgeMatch = cacheControl.match(/(?:s-maxage|max-age)=(\d+)/i);

  return maxAgeMatch ? Number(maxAgeMatch[1]) : null;
}

function buildHopDiagnostics(hops: RedirectHop[]): RedirectHopDiagnostic[] {
  return hops
    .filter((hop) => isRedirectStatus(hop.status) && hop.resolvedLocation)
    .map((hop) => {
      const fromUrl = safeUrl(hop.url);
      const toUrl = hop.resolvedLocation ? safeUrl(hop.resolvedLocation) : null;
      const queryDelta = compareQueryStrings(hop.url, hop.resolvedLocation ?? hop.url);
      const cacheControl = headerValue(hop, "cache-control");

      return {
        hopIndex: hop.index,
        statusType: redirectStatusType(hop.status),
        fromUrl: hop.url,
        toUrl: hop.resolvedLocation,
        relativeLocation: Boolean(hop.location && !/^[a-z][a-z0-9+.-]*:/i.test(hop.location)),
        protocolChange: protocolChange(fromUrl, toUrl),
        hostnameChanged:
          Boolean(fromUrl && toUrl) &&
          fromUrl?.hostname.toLowerCase() !== toUrl?.hostname.toLowerCase(),
        pathChanged: Boolean(fromUrl && toUrl) && fromUrl?.pathname !== toUrl?.pathname,
        queryChanged: !queryDelta.preserved,
        queryAdded: queryDelta.added,
        queryRemoved: queryDelta.removed,
        queryValueChanged: queryDelta.changed,
        trailingSlashChanged: trailingSlashChanged(fromUrl, toUrl),
        setsCookie: Boolean(headerValue(hop, "set-cookie")),
        cacheControl,
        expires: headerValue(hop, "expires"),
        cdnCacheStatus: cdnCacheStatus(hop),
      };
    });
}

function buildDiagnostics(input: RedirectChainAnalysisInput): RedirectChainDiagnostics {
  const hops = input.hops;
  const terminalHop = finalHop(hops);
  const finalUrl = input.finalUrl || terminalHop?.url || input.requestedUrl;
  const hopDiagnostics = buildHopDiagnostics(hops);
  const redirectCookieHops = hopDiagnostics
    .filter((diagnostic) => diagnostic.setsCookie)
    .map((diagnostic) => diagnostic.hopIndex);
  const cacheableTemporaryRedirects = hopDiagnostics
    .filter((diagnostic) => {
      const maxAge = cacheMaxAgeSeconds(diagnostic.cacheControl);

      return diagnostic.statusType === "temporary" && maxAge !== null && maxAge >= 86400;
    })
    .map((diagnostic) => diagnostic.hopIndex);
  const statusBreakdown = hopDiagnostics.reduce<RedirectStatusBreakdown>(
    (breakdown, diagnostic) => {
      if (diagnostic.statusType === "permanent") {
        breakdown.permanent += 1;
      } else if (diagnostic.statusType === "temporary") {
        breakdown.temporary += 1;
      } else if (diagnostic.statusType === "special") {
        breakdown.special += 1;
      }

      return breakdown;
    },
    {
      permanent: 0,
      temporary: 0,
      special: 0,
      finalSuccess: Boolean(terminalHop && terminalHop.status >= 200 && terminalHop.status < 300),
      finalError: Boolean(terminalHop && terminalHop.status >= 400),
    },
  );
  const uniqueHosts = [
    ...new Set(
      hops
        .flatMap((hop) => [
          hostnameOf(hop.url),
          hop.resolvedLocation ? hostnameOf(hop.resolvedLocation) : "",
        ])
        .filter(Boolean),
    ),
  ];
  const uniqueProtocols = [
    ...new Set(
      hops
        .flatMap((hop) => [
          protocolOf(hop.url),
          hop.resolvedLocation ? protocolOf(hop.resolvedLocation) : "",
        ])
        .filter(Boolean),
    ),
  ];

  return {
    urlDelta: buildUrlDelta(input.requestedUrl, finalUrl),
    queryDelta: compareQueryStrings(input.requestedUrl, finalUrl),
    hopDiagnostics,
    statusBreakdown,
    uniqueHosts,
    uniqueProtocols,
    redirectCookieHops,
    cacheableTemporaryRedirects,
    headerSignals: {
      finalContentType: headerValue(terminalHop, "content-type"),
      finalCacheControl: headerValue(terminalHop, "cache-control"),
      finalServer: headerValue(terminalHop, "server"),
      finalVia: headerValue(terminalHop, "via"),
      finalCdnCacheStatus: cdnCacheStatus(terminalHop),
    },
  };
}

export function analyzeRedirectChain(input: RedirectChainAnalysisInput): RedirectChainAnalysis {
  const hops = input.hops;
  const terminalHop = finalHop(hops);
  const finalStatus = terminalHop?.status ?? 0;
  const redirectHops = hops.filter((hop) => isRedirectStatus(hop.status) && hop.resolvedLocation);
  const redirectCount = redirectHops.length;
  const totalDurationMs = hops.reduce((sum, hop) => sum + hop.durationMs, 0);
  const finalUrl = input.finalUrl || terminalHop?.url || input.requestedUrl;
  const temporaryRedirects = redirectHops.filter((hop) =>
    ["temporary", "special"].includes(redirectStatusType(hop.status)),
  );
  const protocols = hops.flatMap((hop) => [protocolOf(hop.url), hop.resolvedLocation ? protocolOf(hop.resolvedLocation) : ""]).filter(Boolean);
  const hasHttpsDowngrade = hops.some(
    (hop) => protocolOf(hop.url) === "https:" && hop.resolvedLocation && protocolOf(hop.resolvedLocation) === "http:",
  );
  const finalProtocol = protocolOf(finalUrl);
  const initialSearch = searchOf(input.requestedUrl);
  const diagnostics = buildDiagnostics(input);
  const queryDelta = diagnostics.queryDelta;
  const hostnames = new Set(
    hops
      .flatMap((hop) => [hostnameOf(hop.url), hop.resolvedLocation ? hostnameOf(hop.resolvedLocation) : ""])
      .filter(Boolean),
  );
  const findings: RedirectChainFinding[] = [];
  let score = 100;

  if (input.terminalReason !== "complete") {
    score -= 40;
    findings.push(
      createFinding(
        "terminal-state",
        input.terminalReason === "redirect-loop"
          ? "Redirect loop detected"
          : input.terminalReason === "too-many-redirects"
            ? "Too many redirects"
            : input.terminalReason === "blocked-redirect"
              ? "Redirect target was blocked"
              : "Redirect response is missing Location",
        "fail",
        "The trace stopped before reaching a normal final response.",
        "Fix the redirect target so the URL resolves to one canonical, public HTTP or HTTPS final URL.",
        terminalHop?.resolvedLocation ?? terminalHop?.location,
      ),
    );
  }

  if (finalStatus >= 200 && finalStatus < 300) {
    findings.push(
      createFinding(
        "final-status",
        "Final response is successful",
        "pass",
        `The chain ends with HTTP ${finalStatus}.`,
        "Keep the canonical destination returning a stable 2xx response.",
        finalUrl,
      ),
    );
  } else if (isRedirectStatus(finalStatus)) {
    score -= 25;
    findings.push(
      createFinding(
        "final-status",
        "Final response is still a redirect",
        "fail",
        `The trace stopped on HTTP ${finalStatus}, so crawlers may not reach a content URL.`,
        "Ensure the final URL returns a 2xx response within the configured redirect limit.",
        finalUrl,
      ),
    );
  } else if (finalStatus >= 400) {
    score -= 35;
    findings.push(
      createFinding(
        "final-status",
        "Final response is an error",
        "fail",
        `The chain ends with HTTP ${finalStatus}.`,
        "Point redirects to a live canonical URL that returns 200-299.",
        finalUrl,
      ),
    );
  } else {
    score -= 20;
    findings.push(
      createFinding(
        "final-status",
        "Final response is unusual",
        "warning",
        `The chain ends with HTTP ${finalStatus || "unknown"}.`,
        "Verify the destination status is intentional and crawlable.",
        finalUrl,
      ),
    );
  }

  if (redirectCount <= 1) {
    findings.push(
      createFinding(
        "redirect-count",
        redirectCount ? "Single redirect" : "No redirects",
        "pass",
        redirectCount
          ? "The URL reaches its destination with one redirect."
          : "The URL resolves directly with no redirect latency.",
        "Keep redirect paths to zero or one hop whenever possible.",
        `${redirectCount} redirect${redirectCount === 1 ? "" : "s"}`,
      ),
    );
  } else {
    const penalty = redirectCount >= 5 ? 25 : redirectCount >= 3 ? 15 : 8;
    score -= penalty;
    findings.push(
      createFinding(
        "redirect-count",
        "Redirect chain has extra hops",
        redirectCount >= 3 ? "fail" : "warning",
        `The URL takes ${redirectCount} redirects before reaching the final response.`,
        "Collapse chained redirects so old URLs point directly to the canonical destination.",
        `${redirectCount} redirects`,
      ),
    );
  }

  if (temporaryRedirects.length) {
    score -= 10;
    findings.push(
      createFinding(
        "redirect-types",
        "Temporary redirects are present",
        "warning",
        "Temporary redirects can be correct for short-lived behavior, but permanent migrations usually need 301 or 308.",
        "Use 301 or 308 for stable canonical URL migrations. Keep 302, 303, and 307 for truly temporary flows.",
        temporaryRedirects.map((hop) => `${hop.status} ${hop.url}`).join("\n"),
      ),
    );
  } else if (redirectCount) {
    findings.push(
      createFinding(
        "redirect-types",
        "Redirects use permanent status codes",
        "pass",
        "The redirect hops use 301 or 308 status codes.",
        "Permanent redirects are usually the right signal for canonical URL changes.",
      ),
    );
  } else {
    findings.push(
      createFinding(
        "redirect-types",
        "No redirect status codes",
        "info",
        "There are no redirect status codes to classify.",
        "No redirect type cleanup is needed for this URL.",
      ),
    );
  }

  if (diagnostics.cacheableTemporaryRedirects.length) {
    score -= 6;
    findings.push(
      createFinding(
        "temporary-cache",
        "Temporary redirect is cacheable for a long time",
        "warning",
        "At least one temporary redirect includes a long max-age or s-maxage directive.",
        "Use permanent redirects for stable migrations, or shorten cache lifetimes for temporary routing.",
        diagnostics.cacheableTemporaryRedirects.map((hopIndex) => `Hop ${hopIndex}`).join(", "),
      ),
    );
  }

  if (hasHttpsDowngrade) {
    score -= 25;
    findings.push(
      createFinding(
        "https-path",
        "HTTPS downgrade detected",
        "fail",
        "At least one hop moves from HTTPS to HTTP.",
        "Never redirect secure HTTPS URLs to insecure HTTP destinations.",
        protocols.join(" -> "),
      ),
    );
  } else if (finalProtocol === "https:") {
    findings.push(
      createFinding(
        "https-path",
        "Final URL uses HTTPS",
        "pass",
        "The redirect path ends on HTTPS and no HTTPS-to-HTTP downgrade was detected.",
        "Keep canonical destinations on HTTPS.",
        finalUrl,
      ),
    );
  } else {
    score -= 15;
    findings.push(
      createFinding(
        "https-path",
        "Final URL is not HTTPS",
        "warning",
        "The chain ends on an HTTP URL.",
        "Use HTTPS as the canonical destination for modern browsers, SEO, and privacy.",
        finalUrl,
      ),
    );
  }

  if (initialSearch && !queryDelta.preserved) {
    score -= 4;
    findings.push(
      createFinding(
        "query-preservation",
        "Query string changed during redirect",
        queryDelta.removed.length || queryDelta.changed.length ? "warning" : "info",
        "The final URL does not preserve the original query parameters exactly.",
        "Preserve important query parameters through redirects unless stripping them is intentional.",
        [
          `Initial: ${queryDelta.initial || "(empty)"}`,
          `Final: ${queryDelta.final || "(empty)"}`,
          queryDelta.removed.length ? `Removed: ${queryDelta.removed.join(", ")}` : "",
          queryDelta.added.length ? `Added: ${queryDelta.added.join(", ")}` : "",
          queryDelta.changed.length ? `Changed values: ${queryDelta.changed.join(", ")}` : "",
        ]
          .filter(Boolean)
          .join("\n"),
      ),
    );
  } else {
    findings.push(
      createFinding(
        "query-preservation",
        initialSearch ? "Query string is preserved" : "No query string to preserve",
        initialSearch ? "pass" : "info",
        initialSearch
          ? "The query string survives the redirect chain."
          : "The requested URL does not include query parameters.",
        "Keep campaign, tracking, and application parameters only when they are needed at the destination.",
      ),
    );
  }

  const splitCanonicalizationHops = diagnostics.hopDiagnostics.filter(
    (diagnostic) =>
      diagnostic.protocolChange !== "same" ||
      diagnostic.hostnameChanged ||
      diagnostic.pathChanged ||
      diagnostic.queryChanged,
  );

  if (redirectCount > 1 && splitCanonicalizationHops.length > 1) {
    score -= 6;
    findings.push(
      createFinding(
        "canonicalization-split",
        "Canonical changes are split across hops",
        "warning",
        "Protocol, host, path, or query cleanup happens over multiple redirects instead of one direct jump.",
        "Point every legacy variant directly at the final canonical URL where your platform allows it.",
        splitCanonicalizationHops
          .map((diagnostic) => `Hop ${diagnostic.hopIndex}: ${diagnostic.fromUrl} -> ${diagnostic.toUrl}`)
          .join("\n"),
      ),
    );
  }

  if (diagnostics.redirectCookieHops.length) {
    score -= 3;
    findings.push(
      createFinding(
        "redirect-cookies",
        "Redirect responses set cookies",
        "warning",
        "One or more redirect hops sets cookies before the final page loads.",
        "Keep public canonical redirects stateless when possible so caches, crawlers, and first visits see consistent behavior.",
        diagnostics.redirectCookieHops.map((hopIndex) => `Hop ${hopIndex}`).join(", "),
      ),
    );
  }

  if (totalDurationMs > 3000) {
    score -= 15;
    findings.push(
      createFinding(
        "timing",
        "Redirect chain is slow",
        "fail",
        `The measured chain took ${totalDurationMs} ms.`,
        "Remove extra hops and move redirect rules closer to the edge.",
        `${totalDurationMs} ms`,
      ),
    );
  } else if (totalDurationMs > 1500) {
    score -= 8;
    findings.push(
      createFinding(
        "timing",
        "Redirect chain could be faster",
        "warning",
        `The measured chain took ${totalDurationMs} ms.`,
        "Shorten the chain and review slow origin or edge rules.",
        `${totalDurationMs} ms`,
      ),
    );
  } else {
    findings.push(
      createFinding(
        "timing",
        "Redirect timing is healthy",
        "pass",
        `The measured chain took ${totalDurationMs} ms.`,
        "Keep redirect rules simple and close to users.",
        `${totalDurationMs} ms`,
      ),
    );
  }

  findings.push(
    createFinding(
      "host-consolidation",
      hostnames.size > 1 ? "Redirect crosses hostnames" : "Hostname stays consistent",
      hostnames.size > 1 ? "info" : "pass",
      hostnames.size > 1
        ? "The redirect path crosses multiple hostnames. That can be fine for www/non-www or domain migrations, but it is worth reviewing."
        : "All observed hops stay on the same hostname.",
      "Keep one canonical hostname and redirect alternates directly to it.",
      [...hostnames].join(" -> "),
    ),
  );

  if (diagnostics.urlDelta.trailingSlashChanged) {
    findings.push(
      createFinding(
        "trailing-slash",
        "Trailing slash is canonicalized",
        "info",
        "The redirect path changes only the trailing slash for the requested URL.",
        "Keep trailing slash behavior consistent in route generation, sitemaps, canonicals, and redirect rules.",
        `${pathnameOf(input.requestedUrl)} -> ${pathnameOf(finalUrl)}`,
      ),
    );
  }

  const boundedScore = Math.max(0, Math.min(100, score));
  const grade = scoreGrade(boundedScore);
  const topFixes = findings
    .filter((finding) => finding.status === "fail" || finding.status === "warning")
    .slice(0, 5);

  return {
    score: boundedScore,
    grade,
    summary: analysisSummary(grade, redirectCount, input.terminalReason),
    finalUrl,
    finalStatus,
    redirectCount,
    totalDurationMs,
    terminalReason: input.terminalReason,
    findings,
    topFixes,
    diagnostics,
  };
}

export function formatRedirectChainReport(input: {
  requestedUrl: string;
  method: RedirectMethod;
  analysis: RedirectChainAnalysis;
  hops: RedirectHop[];
}) {
  const queryDelta = input.analysis.diagnostics.queryDelta;
  const querySummary = queryDelta.preserved
    ? "preserved"
    : [
        queryDelta.removed.length ? `removed ${queryDelta.removed.join(", ")}` : "",
        queryDelta.added.length ? `added ${queryDelta.added.join(", ")}` : "",
        queryDelta.changed.length ? `changed ${queryDelta.changed.join(", ")}` : "",
      ]
        .filter(Boolean)
        .join("; ");

  return [
    "Redirect Chain Report",
    `Requested URL: ${input.requestedUrl}`,
    `Method: ${input.method}`,
    `Final URL: ${input.analysis.finalUrl}`,
    `Final status: ${input.analysis.finalStatus}`,
    `Redirects: ${input.analysis.redirectCount}`,
    `Total time: ${input.analysis.totalDurationMs} ms`,
    `Grade: ${input.analysis.grade}`,
    `Score: ${input.analysis.score}/100`,
    `Terminal reason: ${input.analysis.terminalReason}`,
    "",
    "Hops:",
    ...input.hops.map((hop) => {
      const diagnostic = input.analysis.diagnostics.hopDiagnostics.find(
        (item) => item.hopIndex === hop.index,
      );
      const changeSummary = diagnostic
        ? [
            diagnostic.protocolChange !== "same" ? diagnostic.protocolChange : "",
            diagnostic.hostnameChanged ? "host" : "",
            diagnostic.pathChanged ? "path" : "",
            diagnostic.queryChanged ? "query" : "",
            diagnostic.setsCookie ? "sets-cookie" : "",
          ]
            .filter(Boolean)
            .join(", ")
        : "";

      return [
        `${hop.index}. ${hop.method} ${hop.url}`,
        `   Status: ${hop.status} ${hop.statusText}`,
        `   Time: ${hop.durationMs} ms`,
        hop.resolvedLocation ? `   Location: ${hop.resolvedLocation}` : "",
        changeSummary ? `   Change: ${changeSummary}` : "",
      ]
        .filter(Boolean)
        .join("\n");
    }),
    "",
    "Diagnostics:",
    `- Protocols: ${input.analysis.diagnostics.uniqueProtocols.join(" -> ") || "unknown"}`,
    `- Hosts: ${input.analysis.diagnostics.uniqueHosts.join(" -> ") || "unknown"}`,
    `- Query delta: ${querySummary || "none"}`,
    input.analysis.diagnostics.headerSignals.finalContentType
      ? `- Final content type: ${input.analysis.diagnostics.headerSignals.finalContentType}`
      : "",
    "",
    "Top fixes:",
    ...(input.analysis.topFixes.length
      ? input.analysis.topFixes.map((finding) => `- ${finding.title}: ${finding.recommendation}`)
      : ["- No priority redirect fixes found."]),
  ].join("\n");
}
